import React, { useState, type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Avatar from '@atlaskit/avatar';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import Popup from '@atlaskit/popup';
import { colors } from '@atlaskit/theme';
import { fontFallback } from '@atlaskit/theme/typography';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { taskApprover_softwareReleasesApprovers_jiraVersion$key } from '@atlassian/jira-relay/src/__generated__/taskApprover_softwareReleasesApprovers_jiraVersion.graphql';
import type { taskApprover_softwareReleasesApprovers_jiraVersionApprover$key } from '@atlassian/jira-relay/src/__generated__/taskApprover_softwareReleasesApprovers_jiraVersionApprover.graphql';
import {
	getIgnoreMouseEventTargetSelector,
	getDeclineButtonSelector,
} from '../../common/constants';
import { ApprovalDrawer } from './approval-drawer';
import { ApprovalStatus } from './approval-status';
import messages from './messages';

interface Props {
	jiraVersionApprover: taskApprover_softwareReleasesApprovers_jiraVersionApprover$key;
	jiraVersion: taskApprover_softwareReleasesApprovers_jiraVersion$key;
}

export const TaskApprover = ({ jiraVersionApprover, jiraVersion }: Props) => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const approver = useFragment(
		graphql`
			fragment taskApprover_softwareReleasesApprovers_jiraVersionApprover on JiraVersionApprover {
				user @required(action: THROW) {
					name @required(action: THROW)
					picture
					... on AtlassianAccountUser {
						extendedProfile {
							jobTitle
						}
					}
				}

				...approvalDrawer_softwareReleasesApprovers_jiraVersionApprover
				...approvalStatus_softwareReleasesApprovers
			}
		`,
		jiraVersionApprover,
	);

	const jiraVersionData = useFragment(
		graphql`
			fragment taskApprover_softwareReleasesApprovers_jiraVersion on JiraVersion {
				...approvalDrawer_softwareReleasesApprovers_jiraVersion
			}
		`,
		jiraVersion,
	);

	const { picture, name } = approver.user;
	const role = approver?.user?.extendedProfile?.jobTitle;
	const onWrapperClick = (e: MouseEvent<Element> | React.KeyboardEvent<HTMLDivElement>) => {
		const isOriginDeclineButton =
			e.target instanceof Element && e.target.closest(getDeclineButtonSelector()) != null;
		const isOriginIgnoreMouseEventTarget =
			e.target instanceof Element && e.target.closest(getIgnoreMouseEventTargetSelector()) != null;
		if (isOriginDeclineButton) {
			setIsOpen(true);
			return;
		}
		if (isOriginIgnoreMouseEventTarget) {
			return;
		}

		setIsOpen(!isOpen);
	};
	const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
			e.preventDefault();
			onWrapperClick(e);
		}
	};

	return (
		<>
			<Popup
				content={() => (
					<ApprovalDrawer jiraVersionApprover={approver} jiraVersion={jiraVersionData} />
				)}
				isOpen={isOpen}
				// This prop doesn't work now, but we can use it when the FF is on: https://atlassian.slack.com/archives/CFJ9DU39U/p1688952605900129
				// to make the width of the popup content responsive to the trigger width.
				shouldRenderToParent
				autoFocus
				onClose={() => setIsOpen(false)}
				placement="bottom-start"
				trigger={(triggerProps) => (
					<li>
						<BaseWrapper
							isOpen={isOpen}
							onClick={onWrapperClick}
							onKeyDown={handleKeyDown}
							tabIndex={0}
							role="button"
							{...triggerProps}
						>
							<Avatar src={picture} size="medium" />
							<ApproverWrapper>
								<ApproverName isOpen={isOpen}> {name} </ApproverName>
								<ApproverRole isOpen={isOpen}>{role}</ApproverRole>
							</ApproverWrapper>

							<RightWrapper>
								<ApprovalStatus jiraVersionApprover={approver} />
								{/* eslint-disable-next-line @atlaskit/design-system/no-legacy-icons */}
								<ChevronDownIcon
									primaryColor={isOpen ? token('color.icon.selected', colors.B400) : undefined}
									label={formatMessage(messages.openApproverIcon)}
								/>
							</RightWrapper>
						</BaseWrapper>
					</li>
				)}
			/>
		</>
	);
};

interface isOpenProps {
	isOpen: boolean;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BaseWrapper = styled.div<isOpenProps>({
	display: 'flex',
	gap: token('space.200', '16px'),
	alignItems: 'center',
	padding: `${token('space.100', '8px')} 0 ${token('space.100', '8px')} ${token(
		'space.100',
		'8px',
	)}`,
	cursor: 'pointer',
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: (props: isOpenProps) =>
			!props.isOpen
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					token('color.background.neutral.subtle.hovered', colors.N30A)
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					token('color.background.selected', colors.B50),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ isOpen }: isOpenProps) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		!isOpen ? undefined : token('color.text.selected', colors.B300),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props: isOpenProps) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		!props.isOpen ? 'transparent' : token('color.background.selected', colors.B50),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ApproverWrapper = styled.div({
	width: '214px',
	display: 'flex',
	flexDirection: 'column',
	flex: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ApproverName = styled.span<isOpenProps>({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ isOpen }: isOpenProps) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		!isOpen ? token('color.text', colors.N300) : token('color.text.selected', colors.B300),
	lineHeight: token('space.250', '20px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.body', fontFallback.body.medium),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ApproverRole = styled.div<isOpenProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ isOpen }: isOpenProps) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		!isOpen ? token('color.text.subtlest', colors.N200) : token('color.text.selected', colors.B300),
	lineHeight: token('space.250', '20px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.body.small', fontFallback.body.small),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RightWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});
